/* eslint-disable react-hooks/refs -- the `handleUserClickRef.current =`
 * write below intentionally stabilizes a callback's identity across the
 * `useOpenDM` heartbeat; the inner `useOpenDM` depends on `user` whose
 * identity flips on every typing-status heartbeat. */
import { useCallback, useRef } from "react";
import { useOpenDM } from "@/hooks/use-open-dm";

interface UseChannelUserHandlersResult {
  handleChannelClick: (channelId: string) => void;
  handleUserClick: (userId: string) => void;
}

/**
 * Stable click handlers for `MessageArea` row interactions.
 *
 * - `handleChannelClick` — forwards to the parent's
 *   `onSelectChannel`. Identity is stable per `onSelectChannel`
 *   reference.
 * - `handleUserClick` — opens a 1:1 DM with the clicked user via
 *   `useOpenDM`. The inner hook depends on the live `user` (which
 *   includes typing-status fields and flips identity every
 *   heartbeat), so we capture it through a ref and expose a
 *   stable callback. Critical: without the ref-based stabilization,
 *   every `MessageRow.memo` would bust on every typing heartbeat.
 */
export function useChannelUserHandlers(
  onSelectChannel: ((id: string) => void) | undefined,
): UseChannelUserHandlersResult {
  const handleChannelClick = useCallback(
    (channelId: string) => {
      onSelectChannel?.(channelId);
    },
    [onSelectChannel],
  );

  const handleUserClickInner = useOpenDM(onSelectChannel);
  const handleUserClickRef = useRef(handleUserClickInner);
  handleUserClickRef.current = handleUserClickInner;
  const handleUserClick = useCallback(
    (userId: string) => handleUserClickRef.current(userId),
    [],
  );

  return { handleChannelClick, handleUserClick };
}
